import { BaseTranslation } from 'src/i18n/i18n-types';

export const workspaceSettings: BaseTranslation = {
  settings: 'Settings',
  settingsDescription: 'Settings for your organisation.',
  workspaceSettings: 'Workspace settings',
  workspaceSettingsDescription: 'Organisation information and settings.',
  accountAdministrators: 'Account Administrators',
  companyProfile: 'Company Profile',
  companyProfileDescription: 'Customise your company information.',
  billing: 'Billing',
  billingDescription: 'Manage your subscription.',
  billingDescriptionAlt:
    'Update your payment information or switch plans according to your needs',
  billingDetails: 'Billing Details',
  viewBilling: 'Open Billing',
  team: 'Team',
  teamDescription: 'Manage your team.',
  teamDescriptionAlt:
    'Manage your team members and their account permissions here.',
  teamMembers: 'Team Members',
  upgradePlan: 'Upgrade to Pro',
  upgradeReminder: 'Upgrade your account to get full access to MagicBrief',
  addDetails: 'Add Details',
  subscribed: 'Subscribed',
  paymentExpired: 'Payment expired',
  unlimited: 'Unlimited subscription',
  statuses: {
    enabled: 'Enabled',
    inactive: 'Inactive',
  },
  restrictedWarning: {
    title: 'Workspace Settings Restricted',
    description:
      "You'll need to ask your organisation's admin to edit these settings.",
  },
  editRole: 'Edit Role',
  removeUser: 'Remove User',
  errors: {
    nameRequired: 'A company name is required.',
    missingInviteLinkForRole: 'No invite link for selected role.',
    adminOnly: 'Only available to admins.',
    roleUpdateFailed: 'Failed to update user role: {detail}',
    logoUploadFailed: 'Failed to upload logo: {detail}',
    saveFailed: 'Failed to save your settings: {detail}',
    bigLogo: 'Profile pictures and logos must be less than 500 kB',
    removeUser: 'Failed to remove {name} from your team: {detail}',
    addressRequired: 'An address is required.',
    abnRequired: 'An ABN is required.',
  },
  affiliate: 'Affiliate',
  affiliateDescription: 'Earn 50% commission when you help spread the magic.',
  manage: 'Manage',
  success: {
    roleUpdate: 'Updated role successfully.',
    saved: 'Settings saved.',
    savedWithoutLogo:
      'Settings saved, but your profile picture failed to upload.',
    removeUser: 'Removed {name} from your team.',
  },
  currentPlan: 'Current Plan',
  cancelPlan: 'Cancel Plan',
  plansFromNAMonth: 'Plans from ${price}/mo',
  seats: 'Seats',
  brands: 'Brands',
  viewPlans: 'View Plans',
  viewInvoices: 'View Invoices',
  noInvoices: 'No invoices found',
  usage: 'Usage',
  workspaceUsage: 'Workspace Usage',
  nUsed: '{n} used',
  nIncluded: '{n} included',
  usageLimitNear: 'You are nearing your workspace limit.',
  usageLimitNearDescription:
    'Upgrade your plan to access unlimited workspace usage.',
  usageLimitReached: 'You have reached your workspace usage limit.',
  usageLimitReachedDescription:
    'Upgrade your plan to unlock unlimited workspace usage.',
  upgradePlanMessage:
    'Upgrade your plan to unlock more seats, more tracked brands and better analytics tooling.',
  viewAllPlans: 'View all plans',
  teamManagement: 'Team Management',
  inviteTeamMembers: 'Invite Team Members',
  inviteTeamMembersDescription:
    'Get your projects up and running faster by inviting your team to collaborate.',
  addViaEmailAddress: 'Add Via Email Address',
  inviteTeamMembersFooter:
    'Domain onboarding is enabled, which means new users from your company can automatically join this workspace when they use a work email.',
  addMore: 'Add More',
  yourPlanRenews: 'Your plan renews on {date}',
  information: 'Information',
  upgradeFeatures:
    'Unlock features like real-time insights, in-depth analytics, comprehensive brand tracking, and more! Upgrade now to supercharge your workspace.',
  changeToYearly: 'Change to yearly',
  savePercent: 'Save {percent}%',
  nDaysLeft: '{n} days left',
  youAreOnAFreePlan: 'You are on a free plan.',
  paymentMethods: 'Payment Methods',
  invoiceHistory: 'Invoice History',
  invoice: {
    reference: 'Reference',
    total: 'Total',
    status: 'Status',
    date: 'Date',
    paid: 'Paid',
    pending: 'Pending',
  },
  addNewPaymentMethod: 'Add New Payment Method',
  reAuthenticate: 'Re-authenticate',
  unlimitedUsageTitle:
    'You’re currently on a {plan} plan with unlimited usage.',
  unlimitedUsageDescription:
    'For billing details, please contact your account manager.',
  nPerMonth: '{n}/month',
  billedMonthly: 'Billed monthly',
  billedYearly: 'Billed yearly',
  yourFreeTrialHasExpired: 'Your free trial has expired',
  guestUsers: 'Guest Users',
  manageGuestHere: 'Manage your workspace guest users here',
  guestDescriptionAlt:
    'Invite guest users to collaborate in ‘Insights’ on an account-by-account basis.',
  inviteLink: 'Invite Link',
  shareGuestInvitationLink:
    'Share this link to invite people to this workspace. Guests must create an account before you can grant them access to specific ad accounts.',
  guestAccessAlert:
    'Guest access are granted individually for each ad account through ‘Ad Account Settings’.',
  learnMoreAboutGuestSettings: 'Learn more about adding guests',
  inviteLinkCopiedMessage: 'Invite link copied to clipboard',
  noGuestUsersYet: 'No guest users yet',
  copyYourInvitationLink: 'Copy your invitation link and start collaborating.',
  noAccountAccessYet: 'No account access yet',
  accountAccess: 'Account access',
  changeToEditor: 'Change to ‘Editor’ role',
  removeFromWorkspace: 'Remove from Workspace',
  deleteModal: {
    removoeUserFromWorkspace: 'Remove {name} from workspace? ',
    userDataWillBeLost:
      'This user will lose access to all ad accounts they have access to. They will no longer be able to view or modify any data from this workspace.',
    actionCannotBeUndone:
      "This action cannot be undone. To restore access later, you'll need to re-invite the user and manually reassign ad account access permissions.",
    removeUser: 'Remove User',
    cancel: 'Cancel',
    userRemovedSuccess: 'User {name} removed successfully',
  },
  upgradeModal: {
    changeGuestRole: 'Change {name} to {role} role?',
    userWillHaveAccess:
      'User will have full workspace access, including all ad accounts. This will replace their current limited guest user permissions.',
    previousGuestPermission:
      "Previous guest user permissions will not be saved. If you later downgrade this user back to ‘Guest User’, you'll need to manually reassign specific ad account access.",
    upgradeUser: 'Upgrade User ',
    cancel: 'Cancel',
    userUpgradeSuccess: 'User {name} upgraded successfully',
  },
  downgradeModal: {
    changeGuestRole: 'Change {name} to `Guest` role?',
    userWillHaveLimitedAccess:
      'Guest users have limited access to only the ad accounts they are specifically granted permissions for. This will replace their current workspace-wide access.',
    manuallyAssignAccess:
      "After downgrading access, you'll need to manually grant this user access to specific ad accounts. They will have no account access until you do this.",
    changeToGuest: 'Change to Guest User',
    cancel: 'Cancel',
    userDowngradeSuccess: 'Changed {name} to guest successfully',
  },
};
